import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TraceLogService } from '../services/trace-log.service';

@Injectable()
export class TracingInterceptor implements HttpInterceptor {

  private readonly EXCLUDED_URLS = ['/api/Doc/TraceLog/append'];

  constructor(private traceLogService: TraceLogService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldSkipLogging(req.url)) {
      return next.handle(req);
    }

    const traceId = this.generateHex(16);
    const spanId = this.generateHex(8);
    const traceparent = `00-${traceId}-${spanId}-01`;

    const startTime = this.getISTTime();
    const requestStartMs = Date.now();

    const tracedReq = req.clone({ setHeaders: { traceparent } });

    // No addRequest here: the call is logged once, when it finishes.
    return next.handle(tracedReq).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            const durationMs = Date.now() - requestStartMs;
            this.traceLogService.addResponse({
              traceId,
              uiTraceParentID: traceparent,
              method: req.method,
              url: req.url,
              status: 'success',
              statusCode: event.status,
              startedAt: startTime,
              completedAt: this.getISTTime(),
              requestBody: req.body,
              responseBody: event.body,
              logType: durationMs > 3000 ? 'slow-request' : 'http-response'
            });
          }
        },
        error: (error) => {
          const durationMs = Date.now() - requestStartMs;
          this.traceLogService.addResponse({
            traceId,
            uiTraceParentID: traceparent,
            method: req.method,
            url: req.url,
            status: 'error',
            statusCode: error.status || 0,
            startedAt: startTime,
            completedAt: this.getISTTime(),
            requestBody: req.body,
            responseBody: error.error,
            errorMessage: error.error?.message || error.message || 'Unknown error',
            logType: durationMs > 3000 ? 'slow-request-failed' : 'http-error'
          });
        }
      })
    );
  }

  private shouldSkipLogging(url: string): boolean {
    const lowerUrl = url.toLowerCase();
    return this.EXCLUDED_URLS.some(e => lowerUrl.includes(e.toLowerCase()));
  }

  private getISTTime(): string {
    const istTime = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
    return istTime.toISOString().replace('Z', '+05:30');
  }

  private generateHex(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }
}